// ── Geländehöhen für die Live-Vorschau beim Gebäudezeichnen ──────────────
//
// Liest Höhen aus den offenen Terrarium-Kacheln (AWS Open Data,
// elevation-tiles-prod). Kodierung: hoehe_m = (R*256 + G + B/256) - 32768.
//
// WARUM NICHT DAS BACKEND FRAGEN: backend/utils/elevations.py geht gegen
// opentopodata, und die freie API erlaubt genau 1 Anfrage pro Sekunde — dort
// steckt sogar ein Raten-Gate von 1,05 s drin (Bug #2, 03.07.). Für eine
// Vorschau, die dem Mauszeiger folgt, ist das um Größenordnungen zu langsam.
//
// WARUM NICHT MapLibres queryTerrainElevation: das setzt map.setTerrain()
// voraus, und Terrain-Rendering verzieht die Rasterkacheln. Auf einer Karte,
// auf der Nutzer metergenau Rechtecke zeichnen, ist das ein zu hoher Preis.
// Deshalb dekodieren wir die Kacheln selbst — kein Eingriff in den
// Kartenzustand, volle Kontrolle über den Cache.
//
// GLEICHE QUELLE WIE DAS BACKEND (seit 05.08.2026): backend/utils/elevations.py
// dekodiert dieselben Kacheln mit derselben Formel. Vorschau und Rechnung
// stammen damit per Konstruktion aus derselben Quelle — was hier angezeigt
// wird, ist das, womit gerechnet wird. Wer die Quelle hier wechselt, muss sie
// dort mitziehen (und umgekehrt).
//
// Vorher lief das Backend auf opentopodata-srtm90m: 90 m Raster, auf ganze
// Meter quantisiert. Ein Transekt über 300 m lieferte dort einen EINZIGEN
// Wert — echtes Gelände zwischen Feld und Nachbargebäude konnte diese Quelle
// prinzipbedingt nie sehen.

const TILE_Z = 14;          // ~9,5 m/Pixel bei 50° N; die Daten dahinter sind ~30 m
const TILE_URL = (z, x, y) =>
  `https://s3.amazonaws.com/elevation-tiles-prod/terrarium/${z}/${x}/${y}.png`;

// Kachel-Cache: Schlüssel "z/x/y" -> Promise<ImageData|null>. Wir cachen das
// PROMISE, nicht das Ergebnis, damit parallele Anfragen auf dieselbe Kachel
// (beim Ziehen der Maus der Normalfall) nur einen Download auslösen.
const tileCache = new Map();

function lngLatToTile(lng, lat, z) {
  const n = 2 ** z;
  const x = ((lng + 180) / 360) * n;
  const latRad = (lat * Math.PI) / 180;
  const y = ((1 - Math.asinh(Math.tan(latRad)) / Math.PI) / 2) * n;
  return { fx: x, fy: y, tx: Math.floor(x), ty: Math.floor(y) };
}

function loadTile(z, x, y) {
  const key = `${z}/${x}/${y}`;
  if (tileCache.has(key)) return tileCache.get(key);

  const promise = new Promise((resolve) => {
    const img = new Image();
    // Ohne crossOrigin liefert getImageData einen "tainted canvas"-Fehler.
    // Die Kacheln senden Access-Control-Allow-Origin: * (geprüft 05.08.2026).
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        ctx.drawImage(img, 0, 0);
        resolve(ctx.getImageData(0, 0, img.width, img.height));
      } catch (e) {
        resolve(null);
      }
    };
    // Netzfehler, 404 am Kachelrand, offline: null statt Absturz. Die
    // Vorschau blendet sich dann einfach aus.
    img.onerror = () => resolve(null);
    img.src = TILE_URL(z, x, y);
  });

  tileCache.set(key, promise);
  return promise;
}

// Höhe in Metern über NN, oder null wenn die Kachel nicht ladbar war.
export async function getElevation(lng, lat, z = TILE_Z) {
  const { fx, fy, tx, ty } = lngLatToTile(lng, lat, z);
  const data = await loadTile(z, tx, ty);
  if (!data) return null;

  const px = Math.min(Math.floor((fx - tx) * data.width), data.width - 1);
  const py = Math.min(Math.floor((fy - ty) * data.height), data.height - 1);
  const i = (py * data.width + px) * 4;

  const r = data.data[i];
  const g = data.data[i + 1];
  const b = data.data[i + 2];
  return r * 256 + g + b / 256 - 32768;
}

// Mittlere Höhe über die Ecken eines Polygons — die Bezugshöhe des Spielfelds.
// Bewusst das Eckenmittel und nicht der Schwerpunkt: bei einem Rechteck ist es
// dasselbe, und über mehrere Punkte gemittelt fängt es Rauschen der Kachel ab.
export async function getReferenceElevation(polygonLngLat) {
  if (!polygonLngLat || polygonLngLat.length < 3) return null;

  // Doppelten Schlusspunkt eines geschlossenen Rings ignorieren.
  const first = polygonLngLat[0];
  const last = polygonLngLat[polygonLngLat.length - 1];
  const ring =
    polygonLngLat.length > 1 &&
    Math.abs(first[0] - last[0]) < 1e-9 &&
    Math.abs(first[1] - last[1]) < 1e-9
      ? polygonLngLat.slice(0, -1)
      : polygonLngLat;

  const values = (await Promise.all(ring.map(([lng, lat]) => getElevation(lng, lat))))
    .filter((v) => v !== null);

  if (!values.length) return null;
  return values.reduce((a, b) => a + b, 0) / values.length;
}
